import {getShoppingSubscriptionResultInfo} from '@web/actions/result'
import {DeliveryMethod} from '@web/core/constants'
import {getDeliveryTag} from '@web/helpers/policies'
import makeSlice from '@web/slices/result/sliceMaker'

const slice = makeSlice({
    extraReducers: (builder) => {
        builder.addCase(getShoppingSubscriptionResultInfo.fulfilled, (state, {payload}) => {
            const {
                deliveryAddressBook,
                completedPaymentView,
                expectedPaymentView,
                products,
                arrivalGuarantee: hasArrivalGuaranteeProduct,
                subscriptionApplicationView: {immediatelyPaymentSuccess, orderNo},
            } = payload.subscriptionResultViewInfo
            const deliveryAddress = deliveryAddressBook.defaultDeliveryAddress

            // 즉시결제 케이스에서는 결제 완료된 view로 내려온 정보를 노출한다.
            const paymentView = immediatelyPaymentSuccess
                ? (completedPaymentView ?? {
                      lastTotalPayment: 0,
                      subscriptionDiscountAmount: 0,
                      totalDeliveryFee: 0,
                      totalDiscountPrice: 0,
                      totalProductAmount: 0,
                  })
                : expectedPaymentView

            state.deliveryAddressBook = {
                baseAddress: deliveryAddress.baseAddress,
                detailAddress: deliveryAddress.detailAddress,
                receiverName: deliveryAddress.receiverName,
                telNo1: deliveryAddress.telNo1,
                telNo2: deliveryAddress.telNo2,
                zipCode: deliveryAddress.zipCode,
                locationType: deliveryAddress.pickupLocationType ?? undefined,
                locationDescription: deliveryAddress.pickupLocationContent ?? undefined,
                entryMethodType: deliveryAddress.entryMethodType ?? undefined,
                entryDescription: deliveryAddress.entryMethodContent ?? undefined,
                /** 신청완료는 안심번호 없음. 결제 시 발급되기 때문 */
                virtualPhoneNumber: null,
                /** 안심번호 신청여부 */
                useVirtualPhoneNumber: deliveryAddressBook.onVirtualPhoneNumber,
                deliveryAddressHash: deliveryAddress?.addressId,
            }
            state.firstSubscriptionDiscountAmount = paymentView.subscriptionDiscountAmount ?? 0
            state.totalDeliveryFeeAmount = paymentView.totalDeliveryFee
            state.totalDiscountAmount = paymentView.totalDiscountPrice ?? 0
            state.totalOrderAmount = paymentView.totalProductAmount
            state.pay.totalPayAmount = paymentView.lastTotalPayment
            state.products = products?.map((product) => {
                // 도착보장 정보는 product 당 일대일 매칭이지만 BE에서는 이 정보를 내부 item에 담아서 내려줌
                const arrivalGuarantee = product.items[0]?.arrivalGuarantee ?? null

                return {
                    items: product.items.map((item) => {
                        return {
                            productOrderNo: item.itemNo,
                            optionContents: item.optionContents,
                            price: item.price,
                            productClassType: item.productClassType,
                            quantity: item.quantity,
                            elements: item.elements,
                            /** 옵션상품 배송 메모 */
                            deliveryMemo: deliveryAddressBook.deliveryMemo,
                            /** 탬플릿 메모인지 여부 */
                            templateMemo: false,
                        }
                    }),
                    merchantNo: product.merchantNo,
                    naverBenefit: product.naverBenefit,
                    orderQuantity: 1,
                    productImageUrl: product.productImageUrl ?? '',
                    productName: product.productName,
                    productOrderNo: '',
                    productUrl: product.productUrl ?? '',
                    /** 판매자 채널 이름 */
                    merchantChannelName: product.merchantChannelName,
                    /** 할인이 적용되지 않은 상품 주문 금액 */
                    totalOrderAmount: paymentView.totalProductAmount,
                    /** 지불해야 하는 상품 주문 금액 */
                    totalPayAmount: paymentView.lastTotalPayment,
                    /** 상품 번호 */
                    productNo: product.productNo,
                    /** 옵션을 제외한 지불해야 하는 상품 주문 금액 */
                    productPaymentAmount: paymentView.totalProductAmount,
                    /** 배송메모 */
                    deliveryMemo: deliveryAddressBook.deliveryMemo ?? '',
                    /** 사은품 */
                    gift: product.gift,
                    /** 옵션정보 */
                    additionalInfos: [],
                    /** 도착보장 정보 모델 */
                    arrivalGuarantee,
                    /** 도착보장 정보 모델 */
                    ...(arrivalGuarantee?.deliveryTagType
                        ? {
                              deliveryTagType: getDeliveryTag({
                                  deliveryTagType: arrivalGuarantee.deliveryTagType,
                              }),
                          }
                        : {}),
                    /** 주문제작 상태 */
                    customOrderDetail: null,
                    /** template으로 저장된 배송 메모인지 여부 */
                    templateMemo: false,
                    /** 배송 방법 */
                    deliveryMethodType: DeliveryMethod.DELIVERY,
                    /** 배송시뮬레이터 정보 */
                    deliveryPrediction: null,
                    /** 배송 기한 일시 */
                    dispatchDueDateTime: '',
                    /** 판매자 Account Id */
                    merchantAccountId: '',
                    /** 판매자 아이디 */
                    merchantId: '',
                    /** 서비스 타입 */
                    serviceType: '',
                    /** 상품 카테고리 아이디 */
                    productCategoryId: null,
                    /** 상품 대카테고리 아이디 */
                    largeCategoryId: null,
                    /** 상품 중카테고리 아이디 */
                    middleCategoryId: null,
                    /** 상품 소카테고리 아이디 */
                    smallCategoryId: null,
                    /** 세 카테고리 아이디 */
                    detailedCategoryId: null,
                    /** 네이버 쇼핑 상품 아이디 */
                    ecNvMid: null,
                    /** 주문 완료 시간 */
                    orderDateTime: product.registerDateTime ?? '',
                    /** ProductOrder 상태 */
                    productOrderStatus: '',
                    /** 안전결제 구매자 수수료 */
                    purchaserCommissionAmount: null,
                    /** 상품 판매자 채널 서비스 타입 */
                    merchantChannelServiceType: null,
                    /** 배송 속성 */
                    deliveryAttributeType: product.deliveryAttributeType,
                    overseasDelivery: false,
                    todayDispatch: null,
                    branchBenefit: null,
                    deliveryGroupKey: '',
                    deliveryPolicyApplied: false,
                    ecouponBarcodeConnected: false,
                    hopeDelivery: null,
                    isEcoupon: false,
                }
            })
            state.order = {
                ...state.order,
                arrivalGuaranteeIncluded: hasArrivalGuaranteeProduct,
                orderNo: orderNo ?? '',
                // 즉시결제 성공한 경우에는 배송지 변경이 결제된 것에 대한 변경인지에 대해 혼선이 있을 수 있고
                // 둘다 바꿔주기에는 복잡도가 높아 사용자의 배송지 변경을 구독상세에 위임하기 위해 버튼 미노출
                // https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/12335#issuecomment-2274015
                deliveryChangeable: !immediatelyPaymentSuccess,
            }
        })
    },
})

export default slice.reducer
